import React, { useState, useEffect } from "react";
import { Grid, Button, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { Input, MaskedTextBox } from "@progress/kendo-react-inputs";
import {
  USER_FINANCIALPROFESSIONAL,
  USER_CASEDESIGNER,
  USER_CONCIERGE,
} from "../../shared/constants";
import { getConciergeFinancialProfessionals } from "../../shared/api";
import { isEmailValid } from "../../shared/shared";
import { isEmailAddressDuplicated } from '../../shared/api'
import SuccesDialog from "../success-dialog";

const roleOptions = [
  {
    name: "Case Designer",
    value: USER_CASEDESIGNER,
  },
  {
    name: "Financial Professional",
    value: USER_FINANCIALPROFESSIONAL,
  },
  {
    name: "Concierge",
    value: USER_CONCIERGE,
  },
];

const nameRegex = /^[A-Za-z]+$/;

const getUserRole = (role) => {
  const result = roleOptions.find((i) => i.value === role.toUpperCase());
  return result;
};

const UserDetails = (props) => {
  const [state, setState] = useState({
    isValid: false,
    isLoading: props.user !== null && props.user !== undefined,
    user: {
      id: props.user ? props.user.id : null,
      clientId: props.user ? props.user.clientId : null,
      userId: props.user ? props.user.userId : null,
      firstName: props.user ? props.user.firstName : "",
      lastName: props.user ? props.user.lastName : "",
      email: props.user ? props.user.email : "",
      phone: props.user ? props.user.phone : "",
      role: props.user
        ? getUserRole(props.user.role)
        : {
          name: "Financial Professional",
          value: USER_FINANCIALPROFESSIONAL,
        },
      financialPros: props.user ? props.user.financialPros : [],
      createDate: props.user ? props.user.createDate : new Date(),
      firmName: props.user ? props.user.firmName : "",
    },
  });
  const [validations, setValidations] = useState({})
  const [showDuplicateEmailDialog, setShowDuplicateEmailDialog] = useState(false);

  const ifwUsers = useSelector((state) => state.dataStore.getIn(["ifwUsers"]));

  const token = useSelector((state) =>
    state.dataStore.getIn(["loginInfo", "authorizationToken"])
  );

  const emailValidation = async (email) => {
    if (!isEmailValid(email)) return false;
    const checkForDuplicate = props.user ? props.user.email !== state.user.email : true;

    if (!checkForDuplicate) return true;
    const final = await isEmailAddressDuplicated(email, token);

    return !final
  };

  const firstNameValidation = (firstName) => firstName !== '' && nameRegex.test(firstName);
  const lastNameValidation = (lastName) => lastName !== '' && nameRegex.test(lastName);
  const phoneValidation = (phone) => phone.match(/[\d]/g)?.length === 10;
  const roleValidation = (role) => role.value === USER_CONCIERGE ?
    state.user.financialPros.length > 0 : true

  const valid = async () => {
    const resultEmailValidation = await emailValidation(state.user.email);
    const fieldsValids = {
      email: resultEmailValidation,
      firstName: firstNameValidation(state.user.firstName),
      lastName: lastNameValidation(state.user.lastName),
      phone: phoneValidation(state.user.phone),
      role: roleValidation(state.user.role)
    }
    setValidations(v => {
      return {
        ...v,
        ...fieldsValids
      }
    });

    return !Object.values(fieldsValids).includes(false)
  }

  const handleValidation = (field) => (validation) => (e) => {
    const resultValidation = validation(e.target.value)
    setValidations(items => {
      return {
        ...items,
        [field]: resultValidation
      }
    })
  }

  useEffect(() => {
    if (!state.isLoading) return;

    if (props.user.role.toUpperCase() !== USER_CONCIERGE) {
      setState((s) => ({
        ...s,
        isLoading: false,
      }));
      return;
    }

    getConciergeFinancialProfessionals(props.user.clientId, token).then(
      (response) => {
        const selectedProfessionals = response.map((item) => {
          return {
            name: `${item.firstName} ${item.lastName}`,
            id: item.advisorId,
          };
        });

        setState({
          ...state,
          isLoading: false,
          user: {
            ...state.user,
            financialPros: selectedProfessionals,
          },
        });
      }
    );
  }, []);

  const financialProOptions = ifwUsers
    .toJS()
    .filter((item) => item.role === USER_FINANCIALPROFESSIONAL)
    .map((item) => {
      return {
        name: `${item.firstName} ${item.lastName}`,
        id: item.id,
      };
    });

  const updateUserFirstOrLastName = (field) => {
    return (event) => {
      if (event.value !== "" && !nameRegex.test(event.value)) return;
      setState({
        ...state,
        user: {
          ...state.user,
          [field]: event.value
        }
      });
    };
  }

  const updateUser = (field) => {
    return (event) => {
      setState({
        ...state,
        user: {
          ...state.user,
          [field]: event.value,
        },
      });
    };
  };

  return (
    <Grid className={"user-details-form"} container spacing={3}>
      <Grid item xs={8}>
        {!props.user && <Typography component={"h1"}>Add New User</Typography>}
        {props.user && <Typography component={"h1"}>Edit User</Typography>}
      </Grid>
      <Grid className={"button-container"} item xs={4}>
        <Button className={"cancel-button"} onClick={props.cancel}>
          Cancel
        </Button>
        <Button
          disabled={Object.entries(validations).includes(false)}
          className={!Object.entries(validations).includes(false) ? "" : "disabled-button"}
          onClick={async () => {
            const areFieldsValid = await valid(); // if someone copies and paste input, then the email validator might not have returned yet due to latency. We *must* validate manually, just in case.
            if (!areFieldsValid) return;
            const resultEmailValidation = await emailValidation(state.user.email);
            if (!resultEmailValidation) return;
            !props.user ? props.save(state.user) : props.edit(state.user)
          }}
        > Save
        </Button>
      </Grid>
      <form></form>
      <Grid item xs={12}>
        {!state.isLoading && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Input
                label={"First Name"}
                value={state.user.firstName}
                onChange={(e) => {
                  updateUserFirstOrLastName("firstName")(e);
                  handleValidation("firstName")(firstNameValidation)(e)
                }}
                maxLength={30}
                width={"100%"}
                onBlur={(e) => {
                  handleValidation("firstName")(firstNameValidation)(e)
                }}
                valid={validations.firstName}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                label={"Last Name"}
                value={state.user.lastName}
                onChange={(e) => {
                  updateUserFirstOrLastName("lastName")(e)
                  handleValidation("lastName")(firstNameValidation)(e)
                }}
                onBlur={(e) => {
                  handleValidation("lastName")(firstNameValidation)(e)
                }}
                maxLength={30}
                valid={validations.lastName}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                label={"Email"}
                value={state.user.email}
                onChange={updateUser("email")}
                maxLength={50}
                valid={validations.email}
              />
              {validations.email === false && (<Typography color={'red'}>Invalid email or email is already in use. Please enter a different email </Typography>)}
            </Grid>
            <Grid item xs={12}>
              <MaskedTextBox
                label={"Phone Number"}
                mask="000-000-0000"
                value={state.user.phone}
                onChange={(e) => {
                  updateUser("phone")(e)
                  handleValidation('phone')(phoneValidation)(e)
                }}
                onBlur={(e) => {
                  handleValidation("phone")(phoneValidation)(e)
                }}
                valid={validations.phone}
              />
            </Grid>
            <Grid item xs={12}>
              <DropDownList
                label={"Role"}
                data={roleOptions}
                dataItemKey={"value"}
                textField={"name"}
                value={state.user.role}
                onChange={updateUser("role")}
              />
            </Grid>
            {state.user.role.value === USER_CONCIERGE && (
              <Grid item xs={12}>
                <MultiSelect
                  label={"Financial Professionals"}
                  data={financialProOptions}
                  dataItemKey={"id"}
                  textField={"name"}
                  value={state.user.financialPros}
                  onChange={updateUser("financialPros")}
                  valid={validations.role}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Input
                label={"Firm Name"}
                value={state.user.firmName}
                onChange={updateUser("firmName")}
                maxLength={30}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
      {showDuplicateEmailDialog && <SuccesDialog
        open={true}
        handleOk={() => {
          setShowDuplicateEmailDialog(false);
        }}
        message={"Email is already in use. Please enter a different email."}
      />}
    </Grid>
  );
};

export default UserDetails;
